import { call, put, select, takeLatest } from 'redux-saga/effects'
import { cloneDeep, isEmpty } from 'lodash'
import generateUuid from 'uuid/v4'

import MutationApi from '../../remote/mutationApi'

import { getOrder } from '../../selectors/order'
import { selectRemoteOrderTotal } from '../../selectors/orderTotalRemote'

import { getUserNameFromCard, makeUserAttributes } from '../../utils/orderUtils'
import PaymentTypes from '../../utils/paymentTypes'
import Log from '../../utils/Log'

import {
  orderDetailsRequested,
  splitOrder,
  splitOrderSucceeded,
  splitOrderFailed,
} from '../../actions/order'
import { SplitOrderNumberManager } from '../../utils/orderNumbers'

function customerPaymentPreferenceData(paymentPreference) {
  const customerPaymentId = paymentPreference.uuid
  const paymentType = PaymentTypes.nameFromInteger(paymentPreference.paymentType) || 'cash'

  let paymentDisplay = paymentPreference.shortDescription

  if (PaymentTypes.isMobileWalletPayment(paymentType)) paymentDisplay = paymentDisplay ?? 'Mobile Pay'

  // NOTE (westinschepper): This is temporary
  if (PaymentTypes.isDirectBill(paymentType)) paymentDisplay = 'Payment Pending'
  if (PaymentTypes.isHouseAccount(paymentType)) paymentDisplay = 'House Account'
  if (PaymentTypes.isFnbCredit(paymentType)) paymentDisplay = 'F&B Credit'

  return { customerPaymentId, paymentDisplay, paymentType }
}

function customerPaymentData(paymentPref, paymentTypeParam) {
  if (!isEmpty(paymentPref)) return customerPaymentPreferenceData(paymentPref)

  const paymentType = paymentTypeParam
  const paymentDisplay = paymentTypeParam === 'cash' ? 'Cash' : 'Card Swipe'

  return { customerPaymentId: null, paymentDisplay, paymentType }
}

export function* splitOrderSaga(params) {
  const originalOrderUuid = params?.payload?.orderId ?? ''
  const lineItemUuids = params?.payload?.lineItemIds ?? []
  const newOrderUuid = params?.payload?.newOrderUuid ?? generateUuid()
  const exemptionOverrides = params?.payload?.exemptionOverrides ?? {}
  const isFractionalSplit = params?.payload?.isFractionalSplit ?? false
  const splitTotalInCents = params?.payload?.splitTotalInCents ?? 0
  const selectedOrder = yield select(getOrder, originalOrderUuid)
  const order = cloneDeep(selectedOrder)
  const remoteOrderTotal = yield select(selectRemoteOrderTotal, exemptionOverrides)
  const newOrderNumber = SplitOrderNumberManager.findOrCreate(order.orderNumber)

  // payment logic
  const paymentPreference = params?.payload?.paymentPreference ?? {}
  const paymentTypeParam = params?.payload?.paymentType ?? 'cash'
  const { customerPaymentId, paymentDisplay, paymentType } = customerPaymentData(paymentPreference, paymentTypeParam)

  // if the original order was started via card swipe, we are not doing any auth adjustments/voids for that
  // just need to update stadium data for the new order that was paid for via card swipe
  const cardReaderData = params?.payload?.cardReaderData
  const isCardSwipe = !!cardReaderData

  let paymentParams = {
    amountInCents: isFractionalSplit ? splitTotalInCents : remoteOrderTotal.totalAmountInCents,
    customerPaymentId,
    paymentType,
  }

  let payload = {
    lineItemUuids,
    newOrderNumber,
    newOrderUuid,
    originalOrderUuid,
    isFractionalSplit,
    payments: [ paymentParams ],
    splittableAmount: splitTotalInCents,
  }

  if (isCardSwipe) {
    // update payload to include the fp data in the userAttributes object
    payload = {
      ...payload,
      userAttributes: makeUserAttributes(cardReaderData),
      userName: getUserNameFromCard(cardReaderData),
    }
  }

  try {
    const result = yield call(MutationApi.splitOrder, payload)
    const applied = result?.data?.orderMutations?.[0]?.applied
    if (applied) {
      yield put(splitOrderSucceeded({ originalOrderUuid, newOrderUuid }))
      yield put(orderDetailsRequested(originalOrderUuid))
      yield put(orderDetailsRequested(newOrderUuid))
    } else {
      yield put(splitOrderFailed({ originalOrderUuid, newOrderUuid }))
    }
  } catch(err) {
    yield put(splitOrderFailed({ originalOrderUuid, newOrderUuid }))

    const errorMessage = err?.response?.data?.errorMessage ?? "Sorry, but something went wrong"
    yield handleError(errorMessage, order, originalOrderUuid, newOrderUuid, cardReaderData, paymentType )
  }

  SplitOrderNumberManager.destroy(order.orderNumber)
  SplitOrderNumberManager.cleanup()
}

export function* watchSplitOrderSaga() {
  yield takeLatest(splitOrder.type, splitOrderSaga)
}

function* handleError(errorMessage, order, originalOrderUuid, newOrderUuid, cardReaderData, paymentType) {
  Log.error(`Split order failed for order: ${order?.orderNumber}`, {
    errorMessage,
    originalOrderUuid,
    newOrderUuid,
  })
}
